/* jshint browser:true */
import PerfUtils from "./perf-utils";

/**
 * Utilities to assist with DOM status and management.
 */
export default class DomUtils {
  /**
   * The width and height of an element.
   * @typedef {Object} ElementDimensions
   * @property {number} width The width of the element (px).
   * @property {number} height The height of the element (px).
   */

  /**
   * Returns the width and height of an element.
   * @param {HTMLElement} element The element to use.
   * @param {boolean} includePadding Whether to include padding.
   * @param {boolean} includeBorders Whether to include borders.
   * @returns {ElementDimensions} The element's width and height.
   */
  static getElementDimensions(
    element,
    includePadding = true,
    includeBorders = true
  ) {
    // Include padding and borders
    if (includePadding && includeBorders) {
      return {
        width: element.offsetWidth,
        height: element.offsetHeight,
      };
    }

    // Include padding but not borders
    const widthWithPadding = element.clientWidth;
    const heightWithPadding = element.clientHeight;

    if (includePadding && !includeBorders) {
      return {
        width: widthWithPadding,
        height: heightWithPadding,
      };
    }

    // Include borders but not padding
    const computedStyle = getComputedStyle(element);
    const padding = {
      top: parseFloat(computedStyle.paddingTop),
      right: parseFloat(computedStyle.paddingRight),
      bottom: parseFloat(computedStyle.paddingBottom),
      left: parseFloat(computedStyle.paddingLeft),
    };

    if (includeBorders) {
      const widthWithBorders =
        element.offsetWidth - (padding.left + padding.right);
      const heightWithBorders =
        element.offsetHeight - (padding.top + padding.bottom);

      return {
        width: widthWithBorders,
        height: heightWithBorders,
      };
    }

    // Do not include padding or borders
    const widthNoPaddingOrBorders =
      widthWithPadding - (padding.left + padding.right);
    const heightNoPaddingOrBorders =
      heightWithPadding - (padding.top + padding.bottom);

    return {
      width: widthNoPaddingOrBorders,
      height: heightNoPaddingOrBorders,
    };
  }

  /**
   * Returns whether an element is visible.
   * @param {HTMLElement} element The element to test.
   */
  static isVisible(element) {
    return !!(
      element.offsetWidth ||
      element.offsetHeight ||
      element.getClientRects().length
    );
  }

  /**
   * Returns true if child elements are wider than their containing element.
   * @param {HTMLElement} element The element to test.
   */
  static isOverflowing(element) {
    return element.scrollWidth > element.offsetWidth;
  }

  /**
   * Runs a callback function when an element's visibility has changed.
   * @param {HTMLElement} element The element to observe.
   * @param {Function} callback The function to run when visibility changes.
   */
  static observeVisibility(element, callback) {
    const options = {
      root: document.documentElement,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        callback(entry.intersectionRatio > 0);
      });
    }, options);

    observer.observe(element);
  }

  /**
   * Runs a callback function when an element is resized.
   * @param {HTMLElement} element The element to observe.
   * @param {Function} callback The function to run when a resize occurs.
   */
  static observeResize(element, callback) {
    if ("ResizeObserver" in window) {
      let observeStart = true;

      const resizeObserver = new ResizeObserver(() => {
        if (observeStart) {
          observeStart = false;
          return;
        }

        resizeObserver.disconnect();

        callback();

        observeStart = true;
        requestAnimationFrame(() => resizeObserver.observe(element));
      });

      resizeObserver.observe(element);
    } else {
      window.addEventListener(
        "resize",
        PerfUtils.debounce(() => {
          callback();
        }, 100)
      );
    }
  }
}
